// Johns Hopkins University: public college page (#2652, batch 2), official
// facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Johns Hopkins' Common Data Set 2025-2026 (2025-2026-CDS-Johns-Hopkins-
//    University-v2.pdf from oira.jhu.edu, fetched in a headless browser
//    because the site blocks scripted downloads): C1 applicants/admits/
//    enrollees for Fall 2025 with the residency table; C8 test policy for
//    Fall 2027 (required for the Homewood schools, not required for the
//    Peabody Conservatory); C21 early decision counts; G1/G5 2026-2027 costs
//    (first-year column: the required fee and housing differ from later
//    years).
//  - apply.jhu.edu: Application Deadlines & Requirements (dates for fall 2027
//    entry), Early Decision, and Standardized Testing pages.
//  - College Scorecard, school id 162928 (net price by family income). The
//    API's DEMO_KEY was over its rate limit, so the figures were read from
//    the Scorecard's own school page, which shows the same latest fields.
// Nothing here is profile-derived, a fit score, anyone's odds, or a
// self-reported record. Re-check a figure against its source before editing
// it, and move `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Johns Hopkins Common Data Set 2025-2026',
        url: 'https://oira.jhu.edu/wp-content/uploads/2025-2026-CDS-Johns-Hopkins-University-v2.pdf',
        read: '2026-10-01',
    },
    deadlines: {
        label: 'Johns Hopkins Admissions, Application Deadlines and Requirements',
        url: 'https://apply.jhu.edu/how-to-apply/application-deadlines-requirements/',
        read: '2026-10-01',
    },
    earlyDecision: {
        label: 'Johns Hopkins Admissions, Early Decision',
        url: 'https://apply.jhu.edu/how-to-apply/application-deadlines-requirements/early-decision/',
        read: '2026-10-01',
    },
    tests: {
        label: 'Johns Hopkins Admissions, Standardized Testing',
        url: 'https://apply.jhu.edu/how-to-apply/application-deadlines-requirements/standardized-testing/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?162928-Johns-Hopkins-University',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'johns-hopkins-university',
    kbId: 'johns_hopkins_university',
    name: 'Johns Hopkins University',
    shortName: 'Johns Hopkins',
    aliases: ['JHU', 'Hopkins'],
    location: 'Baltimore, MD',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Johns Hopkins brand colors); decoration only.
    branding: {
        primary: '#002D72',
        secondary: '#68ACE5',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/johns_hopkins_university_logo.png',
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 50259,
        admitted: 3072,
        enrolled: 1378,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 4233, admitted: 199 },
            { group: 'Out-of-state', applied: 35321, admitted: 2469 },
            { group: 'International', applied: 10705, admitted: 404 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Johns Hopkins\' Common Data Set reports one first-year acceptance rate for the whole university, with no rate by school or major.',
        source: 'cds',
    },
    earlyRounds: {
        year: 'Fall 2025',
        title: 'Early decision',
        note: 'The Common Data Set reports Early Decision I and II together for fall 2025.',
        rows: [
            { plan: 'Early Decision I and II', applied: 7639, admitted: 835 },
        ],
        source: 'cds',
        where: 'section C21',
    },

    programs: {
        entering: 'Fall 2027',
        intro: 'Most first-year applicants apply to the Krieger School of Arts and Sciences or the Whiting School of Engineering. One school runs its own process:',
        rows: [
            { school: 'Peabody Institute', requirement: 'Peabody sets its own application requirements and deadlines, published on peabody.jhu.edu.' },
        ],
        source: 'deadlines',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Regular Decision', value: 'January 2' },
        rows: [
            { plan: 'Early Decision I', deadline: 'November 1', decision: 'December 11', binding: true },
            { plan: 'Early Decision II', deadline: 'January 2', decision: 'February 12', binding: true },
            { plan: 'Regular Decision', deadline: 'January 2', decision: 'March 24', binding: false },
        ],
        note: 'The financial aid application is due November 15 for Early Decision I and January 15 for the other two plans. Admitted students reply by January 15 (Early Decision I), February 26 (Early Decision II) or May 1 (Regular Decision).',
        source: 'deadlines',
        binding: 'Early Decision I and II are both binding. You, a parent or guardian and your school counselor sign an agreement that you will enroll if admitted and withdraw your other applications. You can apply early action elsewhere, but if you are admitted to Hopkins early, you must enroll at Hopkins. Hopkins has no early action or rolling admission.',
        bindingSource: 'earlyDecision',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-required',
        blind: false,
        line: 'Johns Hopkins requires first-year applicants to submit SAT or ACT scores. You can type your scores into the application; if you are admitted and enroll, you send official score reports. Hopkins superscores, taking your highest section scores across test dates.',
        source: 'tests',
        cdsLine: 'The Common Data Set marks the SAT or ACT as required for the Homewood schools (Arts and Sciences, Engineering) for fall 2027 applicants, and as not required but considered if submitted for the Peabody Conservatory.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'deadlines',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 68670 },
            { item: 'Required fees (first year)', amount: 500 },
            { item: 'Food and housing (on campus, first year)', amount: 21967 },
            { item: 'Books and supplies', amount: 1377 },
            { item: 'Transportation', amount: 738 },
            { item: 'Other expenses', amount: 1606 },
        ],
        total: 94858,
        note: 'First-year figures. In later years the Common Data Set lists no required fee and $23,338 for food and housing.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 18431,
            byIncome: [
                { income: '$0 to $30,000', amount: 1772 },
                { income: '$30,001 to $48,000', amount: 2136 },
                { income: '$48,001 to $75,000', amount: 4014 },
                { income: '$75,001 to $110,000', amount: 12170 },
                { income: 'Over $110,000', amount: 43081 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Johns Hopkins\' aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
